export type DecorativeAssetCategoryId =
  | 'symbols'
  | 'dividers'
  | 'borders'
  | 'corners'
  | 'background-motifs'
  | 'seasonal-overlays';

export type DecorativeAssetFileType = 'svg' | 'png' | 'webp';
export type DecorativeAssetFit = 'contain' | 'cover' | 'fill';
export type DecorativeAssetPreviewBackground = 'transparent' | 'light' | 'dark' | 'checker';
export type DecorativeAssetSourceLicence =
  | 'repo-original'
  | 'public-domain'
  | 'cc0'
  | 'existing-project-asset'
  | 'unknown';
export type DecorativeAssetBlendMode =
  | 'normal'
  | 'multiply'
  | 'screen'
  | 'overlay'
  | 'soft-light'
  | 'color-dodge';

export interface DecorativeAssetPreset {
  id: string;
  label: string;
  category: DecorativeAssetCategoryId;
  description: string;
  src: string;
  fileType: DecorativeAssetFileType;
  thumbnailSrc?: string;
  previewBackground?: DecorativeAssetPreviewBackground;
  isPlaceholder?: boolean;
  recommendedUse?: string;
  sourceName?: string;
  sourceUrl?: string;
  sourceLicence?: DecorativeAssetSourceLicence;
  attributionRequired?: boolean;
  sourceNotes?: string;
  defaultLayout: {
    w: number;
    h: number;
  };
  defaultOpacity: number;
  defaultBlendMode: DecorativeAssetBlendMode;
  defaultFit: DecorativeAssetFit;
  decorative: boolean;
}

export const DEFAULT_DECORATIVE_ASSET_PRESET_ID = 'divider-fine-gold-rule';

const REPO_ORIGINAL_SOURCE = {
  sourceName: 'Eco3Dcor repository',
  sourceLicence: 'repo-original' as const,
  attributionRequired: false,
  sourceNotes: 'Simple starter SVG created directly in this repository for the built-in decorative asset library.',
};

export const DECORATIVE_ASSET_CATEGORIES: Array<{
  id: DecorativeAssetCategoryId;
  label: string;
  description: string;
}> = [
  {
    id: 'symbols',
    label: 'Symbols',
    description: 'Folklore, celestial, and ornamental symbol art.',
  },
  {
    id: 'dividers',
    label: 'Dividers',
    description: 'Fine rules and editorial separators.',
  },
  {
    id: 'borders',
    label: 'Borders',
    description: 'Framing details for premium panels and compositions.',
  },
  {
    id: 'corners',
    label: 'Corners',
    description: 'Corner flourishes and small ornamental details.',
  },
  {
    id: 'background-motifs',
    label: 'Background motifs',
    description: 'Large subtle motifs for immersive scene backgrounds.',
  },
  {
    id: 'seasonal-overlays',
    label: 'Seasonal overlays',
    description: 'Temporary campaign and event art overlays.',
  },
];

export const DECORATIVE_ASSET_SOURCE_BADGE_LABELS: Record<
  DecorativeAssetSourceLicence,
  string
> = {
  'repo-original': 'Original',
  'public-domain': 'Public domain',
  cc0: 'CC0',
  'existing-project-asset': 'Existing',
  unknown: 'Unknown',
};

export const DECORATIVE_ASSET_PRESETS: DecorativeAssetPreset[] = [
  {
    id: DEFAULT_DECORATIVE_ASSET_PRESET_ID,
    label: 'Fine gold rule',
    category: 'dividers',
    description: 'Repo-original fine gold divider for editorial composition.',
    src: '/decorative/dividers/fine-gold-rule.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Small editorial divider below headings or between short text lockups.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 10, h: 1 },
    defaultOpacity: 0.8,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'divider-celestial-dot',
    label: 'Celestial dot divider',
    category: 'dividers',
    description: 'Repo-original divider with a readable astrolabe-style centre mark.',
    src: '/decorative/dividers/celestial-dot-divider.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Premium section dividers and small ceremonial separators.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 10, h: 1 },
    defaultOpacity: 0.74,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'divider-ornamental-line',
    label: 'Ornamental line',
    category: 'dividers',
    description: 'Repo-original ornamental line divider with heavier thumbnail-readable strokes.',
    src: '/decorative/dividers/ornamental-line-divider.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Editorial separators where a slightly more decorative line is needed.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 12, h: 1 },
    defaultOpacity: 0.72,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'divider-raised-gold',
    label: 'Raised gold divider',
    category: 'dividers',
    description: 'Repo-original raised gold divider with a stronger central ornament.',
    src: '/decorative/dividers/raised-gold-divider.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Luxury divider under headings, hero lockups, or compact editorial sections.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 12, h: 1 },
    defaultOpacity: 0.78,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'divider-folk-ornament',
    label: 'Folk ornament divider',
    category: 'dividers',
    description: 'Repo-original folk-inspired ornamental divider with heavier readable forms.',
    src: '/decorative/dividers/folk-ornament-divider.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Mythic or folklore editorial separators where a warmer ornament is useful.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 12, h: 1 },
    defaultOpacity: 0.74,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'divider-double-rule',
    label: 'Double rule divider',
    category: 'dividers',
    description: 'Repo-original double-line rule with small central gold detail.',
    src: '/decorative/dividers/double-rule-divider.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Clean, structured divider for text groups, cards, and footer lockups.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 12, h: 1 },
    defaultOpacity: 0.72,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'divider-celestial-arc',
    label: 'Celestial arc divider',
    category: 'dividers',
    description: 'Repo-original celestial divider with an arched astrolabe-style centre.',
    src: '/decorative/dividers/celestial-arc-divider.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Ceremonial section divider for darker premium or mythic compositions.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 11, h: 1 },
    defaultOpacity: 0.72,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'divider-minimal-dot-rule',
    label: 'Minimal dot rule',
    category: 'dividers',
    description: 'Repo-original minimal gold rule with centred dot detail.',
    src: '/decorative/dividers/minimal-dot-rule.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Quiet divider for restrained layouts where ornament should stay subtle.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 10, h: 1 },
    defaultOpacity: 0.76,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'divider-whisker-diamond',
    label: 'Whisker diamond divider',
    category: 'dividers',
    description: 'Repo-original fine whisker rule with a centred diamond accent and terminal dots.',
    src: '/decorative/dividers/whisker-diamond-divider.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Subtle editorial separator below eyebrows or short heading lockups.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 10, h: 1 },
    defaultOpacity: 0.78,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'divider-astral-reticle',
    label: 'Astral reticle divider',
    category: 'dividers',
    description: 'Repo-original astrolabe-style reticle with flanking horizon lines.',
    src: '/decorative/dividers/astral-reticle-divider.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Ceremonial divider for mythic or celestial editorial sections.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 12, h: 1 },
    defaultOpacity: 0.76,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'border-fine-gallery',
    label: 'Fine gallery border',
    category: 'borders',
    description: 'Repo-original transparent border for framed editorial panels.',
    src: '/decorative/borders/fine-gallery-border.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Thin frame treatment around composed image or copy areas.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 10, h: 6 },
    defaultOpacity: 0.44,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'border-celestial-panel',
    label: 'Celestial panel border',
    category: 'borders',
    description: 'Repo-original panel border with celestial anchor points.',
    src: '/decorative/borders/celestial-panel-border.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Subtle ceremonial frame for premium scene or content panels.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 10, h: 6 },
    defaultOpacity: 0.42,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'border-raised-gallery',
    label: 'Raised gallery border',
    category: 'borders',
    description: 'Repo-original raised border for framed premium panels.',
    src: '/decorative/borders/raised-gallery-border.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Framing content panels, product feature areas, or editorial image frames.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 10, h: 6 },
    defaultOpacity: 0.46,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'border-double-line',
    label: 'Double line border',
    category: 'borders',
    description: 'Repo-original double-line border with a clean gallery feel.',
    src: '/decorative/borders/double-line-border.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Structured panel frames where a quiet border is preferable.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 10, h: 6 },
    defaultOpacity: 0.42,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'border-celestial-frame',
    label: 'Celestial frame border',
    category: 'borders',
    description: 'Repo-original celestial frame with arched details and gold anchor points.',
    src: '/decorative/borders/celestial-frame-border.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Feature panels, immersive hero compositions, or mythic campaign frames.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 11, h: 6 },
    defaultOpacity: 0.38,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'border-soft-panel',
    label: 'Soft panel border',
    category: 'borders',
    description: 'Repo-original soft rounded border for understated framed areas.',
    src: '/decorative/borders/soft-panel-border.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Subtle panel framing where a gentler border shape is needed.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 10, h: 6 },
    defaultOpacity: 0.36,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'corner-gilded-mark',
    label: 'Gilded corner mark',
    category: 'corners',
    description: 'Repo-original corner ornament for premium layouts.',
    src: '/decorative/corners/gilded-corner-mark.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Single-corner emphasis or paired frame detailing.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 4, h: 4 },
    defaultOpacity: 0.66,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'corner-celestial-mark',
    label: 'Celestial corner mark',
    category: 'corners',
    description: 'Repo-original corner ornament with astrolabe-like detail.',
    src: '/decorative/corners/celestial-corner-mark.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Corner detail for mythic or celestial compositions.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 4, h: 4 },
    defaultOpacity: 0.62,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'corner-raised-gold',
    label: 'Raised gold corner',
    category: 'corners',
    description: 'Repo-original raised corner mark with heavier gold detailing.',
    src: '/decorative/corners/raised-gold-corner.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Corner accent for premium frames, panels, or editorial compositions.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 4, h: 4 },
    defaultOpacity: 0.68,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'corner-double-line',
    label: 'Double line corner',
    category: 'corners',
    description: 'Repo-original double-line corner accent for cleaner frames.',
    src: '/decorative/corners/double-line-corner.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Pair with double-line borders or use as restrained frame accents.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 3, h: 3 },
    defaultOpacity: 0.64,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'corner-celestial-arc',
    label: 'Celestial arc corner',
    category: 'corners',
    description: 'Repo-original celestial arc corner with astrolabe-style detail.',
    src: '/decorative/corners/celestial-arc-corner.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Mythic or celestial corner accent for dark editorial layouts.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 4, h: 4 },
    defaultOpacity: 0.62,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'corner-folk-scroll',
    label: 'Folk scroll corner',
    category: 'corners',
    description: 'Repo-original folk scroll corner with stronger ornamental curves.',
    src: '/decorative/corners/folk-scroll-corner.svg',
    fileType: 'svg',
    previewBackground: 'checker',
    recommendedUse: 'Warm folklore corner flourish for editorial frames and section accents.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 4, h: 4 },
    defaultOpacity: 0.62,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'motif-folklore-wall-placeholder',
    label: 'Folklore wall motif',
    category: 'background-motifs',
    description: 'Placeholder repo-original large wall motif until final Eco3Dcor symbol art is created.',
    src: '/decorative/motifs/folklore-wall-motif-placeholder.svg',
    fileType: 'svg',
    previewBackground: 'dark',
    isPlaceholder: true,
    recommendedUse: 'Large, low-opacity immersive background motif behind hero scenes.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 18, h: 10 },
    defaultOpacity: 0.16,
    defaultBlendMode: 'soft-light',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'motif-celestial-map',
    label: 'Celestial map motif',
    category: 'background-motifs',
    description: 'Repo-original celestial map motif for subtle immersive backgrounds.',
    src: '/decorative/motifs/celestial-map-motif.svg',
    fileType: 'svg',
    previewBackground: 'dark',
    recommendedUse: 'Large low-opacity wall or hero background artwork behind content.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 18, h: 10 },
    defaultOpacity: 0.18,
    defaultBlendMode: 'soft-light',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'motif-soft-arc-wall',
    label: 'Soft arc wall motif',
    category: 'background-motifs',
    description: 'Repo-original soft arc motif for quiet wall texture and depth.',
    src: '/decorative/motifs/soft-arc-wall-motif.svg',
    fileType: 'svg',
    previewBackground: 'dark',
    recommendedUse: 'Subtle background structure behind hero scenes or editorial sections.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 18, h: 9 },
    defaultOpacity: 0.16,
    defaultBlendMode: 'soft-light',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'motif-folk-orbit',
    label: 'Folk orbit motif',
    category: 'background-motifs',
    description: 'Repo-original orbit motif with broad folklore geometry.',
    src: '/decorative/motifs/folk-orbit-motif.svg',
    fileType: 'svg',
    previewBackground: 'dark',
    recommendedUse: 'Large backdrop motif for mythic or gallery-style compositions.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 16, h: 8 },
    defaultOpacity: 0.18,
    defaultBlendMode: 'soft-light',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'seasonal-subtle-star-field',
    label: 'Subtle star field',
    category: 'seasonal-overlays',
    description: 'Repo-original transparent star field overlay for seasonal or event compositions.',
    src: '/decorative/seasonal-overlays/subtle-star-field.svg',
    fileType: 'svg',
    previewBackground: 'dark',
    recommendedUse: 'Large quiet overlay across dark sections or temporary campaign pages.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 20, h: 10 },
    defaultOpacity: 0.24,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'seasonal-winter-sparkle-field',
    label: 'Winter sparkle field',
    category: 'seasonal-overlays',
    description: 'Repo-original transparent winter sparkle field for seasonal compositions.',
    src: '/decorative/seasonal-overlays/winter-sparkle-field.svg',
    fileType: 'svg',
    previewBackground: 'dark',
    recommendedUse: 'Temporary winter or launch overlay where subtle sparkle is useful.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 20, h: 10 },
    defaultOpacity: 0.24,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'symbol-folklore-mixed-placeholder',
    label: 'Folklore mixed symbols',
    category: 'symbols',
    description: 'Placeholder repo-original folklore symbol composition until final Eco3Dcor symbols are created.',
    src: '/decorative/symbols/folklore-mixed-placeholder.svg',
    fileType: 'svg',
    previewBackground: 'dark',
    isPlaceholder: true,
    recommendedUse: 'Subtle symbol layer on dark wall backgrounds.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 12, h: 7 },
    defaultOpacity: 0.22,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
  {
    id: 'symbol-celestial-placeholder',
    label: 'Celestial symbols',
    category: 'symbols',
    description: 'Placeholder repo-original celestial symbol composition until final Eco3Dcor symbols are created.',
    src: '/decorative/symbols/celestial-placeholder.svg',
    fileType: 'svg',
    previewBackground: 'dark',
    isPlaceholder: true,
    recommendedUse: 'Subtle astrolabe-style symbol layer on dark wall backgrounds.',
    ...REPO_ORIGINAL_SOURCE,
    defaultLayout: { w: 10, h: 7 },
    defaultOpacity: 0.2,
    defaultBlendMode: 'screen',
    defaultFit: 'contain',
    decorative: true,
  },
];

export const DECORATIVE_ASSET_CATEGORY_OPTIONS = DECORATIVE_ASSET_CATEGORIES.map((category) => ({
  value: category.id,
  label: category.label,
}));

export const DECORATIVE_ASSET_FIT_OPTIONS: Array<{ value: DecorativeAssetFit; label: string }> = [
  { value: 'contain', label: 'Contain full asset' },
  { value: 'cover', label: 'Cover frame' },
  { value: 'fill', label: 'Stretch to frame' },
];

export const DECORATIVE_ASSET_BLEND_MODE_OPTIONS: Array<{
  value: DecorativeAssetBlendMode;
  label: string;
}> = [
  { value: 'normal', label: 'Normal' },
  { value: 'multiply', label: 'Multiply' },
  { value: 'screen', label: 'Screen' },
  { value: 'overlay', label: 'Overlay' },
  { value: 'soft-light', label: 'Soft light' },
  { value: 'color-dodge', label: 'Colour dodge' },
];

export function resolveDecorativeAssetCategory(value: unknown): DecorativeAssetCategoryId {
  return DECORATIVE_ASSET_CATEGORIES.some((category) => category.id === value)
    ? (value as DecorativeAssetCategoryId)
    : 'dividers';
}

export function resolveDecorativeAssetFit(value: unknown): DecorativeAssetFit {
  return value === 'cover' || value === 'fill' || value === 'contain' ? value : 'contain';
}

export function resolveDecorativeAssetBlendMode(value: unknown): DecorativeAssetBlendMode {
  return DECORATIVE_ASSET_BLEND_MODE_OPTIONS.some((option) => option.value === value)
    ? (value as DecorativeAssetBlendMode)
    : 'normal';
}

export function findDecorativeAssetPreset(value: unknown): DecorativeAssetPreset | null {
  if (typeof value !== 'string') return null;
  return DECORATIVE_ASSET_PRESETS.find((preset) => preset.id === value) ?? null;
}

export function getDecorativeAssetPreset(
  value: unknown,
  fallbackId = DEFAULT_DECORATIVE_ASSET_PRESET_ID
): DecorativeAssetPreset {
  return (
    findDecorativeAssetPreset(value) ??
    findDecorativeAssetPreset(fallbackId) ??
    DECORATIVE_ASSET_PRESETS[0]
  );
}

export function getDecorativeAssetPresetsByCategory(
  category: DecorativeAssetCategoryId
): DecorativeAssetPreset[] {
  return DECORATIVE_ASSET_PRESETS.filter((preset) => preset.category === category);
}

export function decorativeAssetPresetToBlockContent(preset: DecorativeAssetPreset) {
  return {
    assetPresetId: preset.id,
    assetCategory: preset.category,
    assetSrc: preset.src,
    thumbnailSrc: preset.thumbnailSrc,
    assetFileType: preset.fileType,
    previewBackground: preset.previewBackground,
    isPlaceholder: preset.isPlaceholder === true,
    recommendedUse: preset.recommendedUse,
    sourceName: preset.sourceName,
    sourceUrl: preset.sourceUrl,
    sourceLicence: preset.sourceLicence,
    attributionRequired: preset.attributionRequired,
    sourceNotes: preset.sourceNotes,
    assetAlt: '',
    decorative: preset.decorative,
    fit: preset.defaultFit,
  };
}

export function decorativeAssetPresetToBlockStyle(preset: DecorativeAssetPreset) {
  return {
    opacity: preset.defaultOpacity,
    blendMode: preset.defaultBlendMode,
    rotation: 0,
    flipX: false,
    flipY: false,
    blur: 0,
    lockAspectRatio: true,
  };
}
